import { Alert, Box, Button, Grid, GridItem, Link, Tooltip, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import type { Log } from 'types/api/log';

import { route } from 'nextjs-routes';

import { space } from 'lib/html-entities';
import { useI18n } from 'lib/i18n/context';
import Skeleton from 'ui/shared/chakra/Skeleton';
import AddressEntity from 'ui/shared/entities/address/AddressEntity';
import TxEntity from 'ui/shared/entities/tx/TxEntity';
import { SHARED_LOGS_KEYS } from 'ui/shared/logs/i18n';
import LogDecodedInputData from 'ui/shared/logs/LogDecodedInputData';
import LogTopic from 'ui/shared/logs/LogTopic';
import type { DataType } from 'ui/shared/RawInputData';
import RawInputData from 'ui/shared/RawInputData';

type Props = Log & {
  type: 'address' | 'transaction';
  isLoading?: boolean;
  defaultDataType?: DataType;
};

const RowHeader = ({ children, isLoading }: { children: React.ReactNode; isLoading?: boolean }) => {
  const labelColor = useColorModeValue('#616161', '#999999');

  return (
    <GridItem _notFirst={{ my: { base: 4, lg: 0 } }}>
      <Skeleton
        fontWeight={ 500 }
        isLoaded={ !isLoading }
        display="inline-block"
        color={ labelColor }
        fontFamily="'Space Grotesk', sans-serif"
        fontSize="14px"
        lineHeight="18px"
      >
        { children }
      </Skeleton>
    </GridItem>
  );
};

const LogItem = ({ address, index, topics, data, decoded, type, transaction_hash: txHash, isLoading, defaultDataType }: Props) => {
  const { t } = useI18n();
  const cardBg = useColorModeValue('#FFFFFF', '#111111');
  const cardBorderColor = useColorModeValue('#E9ECEF', '#222222');
  const valueColor = useColorModeValue('#000000', '#FFFFFF');
  const mutedTextColor = useColorModeValue('#616161', '#999999');
  const subtlePanelBg = useColorModeValue('#FBFCFE', '#171717');
  const alertBg = useColorModeValue('#FBFCFE', '#171717');
  const indexBadgeBg = useColorModeValue('#FBFCFE', '#171717');

  const hasTxInfo = type === 'address' && txHash;

  return (
    <Box
      bg={ cardBg }
      border="1px solid"
      borderColor={ cardBorderColor }
      borderRadius="8px"
      px={ 4 }
      py={ 4 }
      _notFirst={{ mt: 4 }}
    >
      <Grid
        gridTemplateColumns={{ base: 'minmax(0, 1fr)', lg: '200px minmax(0, 1fr)' }}
        rowGap={ 4 }
        columnGap={{ base: 0, lg: '80px' }}
        sx={{
          '& a': {
            color: valueColor,
          },
          '& button[aria-label="Copy value"]': {
            color: '#14DCAC',
          },
        }}
      >
        { !decoded && !address.is_verified && type === 'transaction' && (
          <GridItem colSpan={{ base: 1, lg: 2 }}>
            <Alert
              status="warning"
              display="inline-table"
              whiteSpace="normal"
              bg={ alertBg }
              border="1px solid"
              borderColor={ cardBorderColor }
              color={ mutedTextColor }
              fontFamily="'Space Grotesk', sans-serif"
              fontSize="14px"
              lineHeight="18px"
              fontWeight={ 500 }
            >
              { t(SHARED_LOGS_KEYS.notice.verifyContract) }{ space }
              <Link href={ route({ pathname: '/address/[hash]/contract-verification', query: { hash: address.hash } }) }>
                { t(SHARED_LOGS_KEYS.actions.verifyContract) }
              </Link>
            </Alert>
          </GridItem>
        ) }

        { hasTxInfo ?
          <RowHeader isLoading={ isLoading }>{ t(SHARED_LOGS_KEYS.fields.transaction) }</RowHeader> :
          <RowHeader isLoading={ isLoading }>{ t(SHARED_LOGS_KEYS.fields.address) }</RowHeader> }

        <GridItem display="flex" alignItems="center" minW={ 0 }>
          { type === 'address' && txHash ? (
            <TxEntity
              hash={ txHash }
              isLoading={ isLoading }
              mr={{ base: 6, lg: 4 }}
            />
          ) : (
            <AddressEntity
              address={ address }
              isLoading={ isLoading }
              mr={{ base: 6, lg: 4 }}
            />
          ) }
          <Skeleton isLoaded={ !isLoading } ml="auto" borderRadius="base">
            <Tooltip label={ t(SHARED_LOGS_KEYS.tooltip.logIndex) }>
              <Button
                size="sm"
                fontWeight={ 500 }
                h="24px"
                minW="24px"
                px="8px"
                borderRadius="4px"
                bg={ indexBadgeBg }
                border="1px solid"
                borderColor={ cardBorderColor }
                color={ mutedTextColor }
                _hover={{ bg: indexBadgeBg }}
              >
                { index }
              </Button>
            </Tooltip>
          </Skeleton>
        </GridItem>

        { decoded && (
          <>
            <RowHeader isLoading={ isLoading }>{ t(SHARED_LOGS_KEYS.fields.decodedInputData) }</RowHeader>
            <GridItem>
              <Box
                fontFamily="'Space Grotesk', sans-serif"
                color={ valueColor }
                sx={{
                  '& > *': { w: '100%' },
                  '& .chakra-divider': { borderColor: cardBorderColor },
                  '& .chakra-tag': {
                    bg: subtlePanelBg,
                    border: '1px solid',
                    borderColor: cardBorderColor,
                    borderRadius: '4px',
                    color: mutedTextColor,
                    fontFamily: '\'Space Grotesk\', sans-serif',
                    fontSize: '12px',
                    fontWeight: 500,
                  },
                  '& .chakra-grid': {
                    bg: subtlePanelBg,
                    border: '1px solid',
                    borderColor: cardBorderColor,
                    borderRadius: '8px',
                  },
                }}
              >
                <LogDecodedInputData data={ decoded } isLoading={ isLoading }/>
              </Box>
            </GridItem>
          </>
        ) }

        <RowHeader isLoading={ isLoading }>{ t(SHARED_LOGS_KEYS.fields.topics) }</RowHeader>
        <GridItem>
          <Box
            bg={ subtlePanelBg }
            border="1px solid"
            borderColor={ cardBorderColor }
            borderRadius="8px"
            px={ 4 }
            py={ 3 }
          >
            { topics.filter(Boolean).map((item, topicIndex) => (
              <LogTopic
                key={ topicIndex }
                hex={ item }
                index={ topicIndex }
                isLoading={ isLoading }
              />
            )) }
          </Box>
        </GridItem>

        <RowHeader isLoading={ isLoading }>{ t(SHARED_LOGS_KEYS.fields.data) }</RowHeader>
        <GridItem>
          { defaultDataType ? (
            <Box
              fontFamily="'Space Grotesk', sans-serif"
              color={ valueColor }
              sx={{
                '& section > .chakra-flex': { mb: 2 },
              }}
            >
              <RawInputData hex={ data } isLoading={ isLoading } defaultDataType={ defaultDataType } minHeight="53px"/>
            </Box>
          ) : (
            <Skeleton
              isLoaded={ !isLoading }
              p={ 4 }
              fontSize="14px"
              lineHeight="18px"
              fontFamily="'Space Grotesk', sans-serif"
              borderRadius="8px"
              bgColor={ isLoading ? undefined : subtlePanelBg }
              border="1px solid"
              borderColor={ cardBorderColor }
              color={ valueColor }
              wordBreak="break-all"
            >
              { data }
            </Skeleton>
          ) }
        </GridItem>
      </Grid>
    </Box>
  );
};

export default React.memo(LogItem);
